'use client';
import React, { useEffect } from 'react';
import { ModuleWrapper } from './ModuleWrapper';
import { ModuleTable } from './ModuleTable';
import { SessionContext } from '@benzinga/session-context';
import { CalendarManager, Earnings as BaseEarnings } from '@benzinga/calendar-manager';
import { QuotesManager } from '@benzinga/quotes-manager';
import { DateTime } from 'luxon';
import { formatDatePretty } from '@benzinga/date-utils';
import { TickerCell, TextCell, TimeCell } from './TableCells';
import type { ColumnsType } from 'antd/es/table';

interface Earnings extends BaseEarnings {
  marketCap: number;
}

enum MarketTimeStatus {
  BEFORE_MARKET = 'Before Market Open',
  MARKET_TIME = 'Market Time',
  AFTER_MARKET = 'After Market Close',
}

const getMarketTimeStatus = (time: string): MarketTimeStatus => {
  const marketOpen = DateTime.fromObject({ hour: 9, minute: 30 });
  const marketClose = DateTime.fromObject({ hour: 16, minute: 0 });
  const currentTime = DateTime.fromFormat(time, 'HH:mm:ss');

  if (currentTime < marketOpen) {
    return MarketTimeStatus.BEFORE_MARKET;
  } else if (currentTime >= marketOpen && currentTime <= marketClose) {
    return MarketTimeStatus.MARKET_TIME;
  } else {
    return MarketTimeStatus.AFTER_MARKET;
  }
};

const PeriodBadge: React.FC<{ period: 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'FY' }> = ({ period }) => {
  const positions = { Q1: 'left-0', Q2: 'left-[28px]', Q3: 'left-[56px]', Q4: 'left-[84px]' };

  return (
    <>
    <div className="relative rounded w-[112px] h-6 bg-[#f4f8ff] border border-[#deebff]">
      <div
        className={`absolute -top-px ${positions[period]} rounded bg-blue-500 w-7 h-6 text-white flex items-center justify-center font-bold text-sm z-10`}
      >
        {period}
      </div>
      <span className="absolute left-[28px] top-[5px] w-px h-3 border-l border-gray-200" />
      <span className="absolute left-[56px] top-[5px] w-px h-3 border-l border-gray-200" />
      <span className="absolute left-[84px] top-[5px] w-px h-3 border-l border-gray-200" />
    </div>
  </>
  );
};  

const BUCKET_MAX_SIZE = 10;

const columns: ColumnsType<Earnings> = [
  {
    key: 'ticker',
    title: 'Ticker',
    dataIndex: 'ticker',
    render: (_value, record) => <TickerCell ticker={record.ticker} name={record.name} />,
  },
  {
    key: 'date',
    title: 'Date',
    dataIndex: 'date',
    render: (_value, record) => (
      <div>
        <TextCell>
          {record.date
            ? formatDatePretty(record.date, { timePeriod: { month: 'short', day: 'numeric', year: 'numeric' } })
            : '--'}
        </TextCell>
        <TimeCell time={record.time} />
      </div>
    ),
  },
  {
    key: 'time',
    title: 'Timeframe',
    dataIndex: 'time',
    render: (_value, record) => <TextCell>{getMarketTimeStatus(record.time)}</TextCell>,
  },
  {
    key: 'period',
    title: 'Period',
    dataIndex: 'period',
    render: (_value, record) => <PeriodBadge period={record.period} />,
  },
];

const TopEarningsModule: React.FC = () => {
  const session = React.useContext(SessionContext);
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<Earnings[]>([]);

  useEffect(() => {
    const fetchEarningsData = async () => {
      try {
        const calendarManager = session.getManager(CalendarManager);
        const response = await calendarManager.getCalendarData(
          'earnings',
          { date: DateTime.now().toFormat('yyyy-LL-dd') },
          true,
        );

        const earningsData = response?.ok ?? [];

        const preMarket: Earnings[] = [];
        const afterMarket: Earnings[] = [];

        const quotesManager = session.getManager(QuotesManager);
        const quoteResponse = await quotesManager.getDelayedQuotes(earningsData.map(earning => earning.ticker));
        const quotes = quoteResponse?.ok ?? {};

        earningsData.forEach(earning => {
          const marketCap = quotes[earning.ticker]?.marketCap ?? 0;
          const marketTimeStatus = getMarketTimeStatus(earning.time);

          if (marketTimeStatus === MarketTimeStatus.BEFORE_MARKET) {
            preMarket.push({ ...earning, marketCap });
          } else if (marketTimeStatus === MarketTimeStatus.AFTER_MARKET) {
            afterMarket.push({ ...earning, marketCap });
          }
        });

        const sortTopEarnings = (earnings: Earnings[]) =>
          earnings
            .sort((a, b) => b.marketCap - a.marketCap)
            .slice(0, BUCKET_MAX_SIZE)
            .sort((a, b) => DateTime.fromISO(a.time).toMillis() - DateTime.fromISO(b.time).toMillis());

        setData([...sortTopEarnings(preMarket), ...sortTopEarnings(afterMarket)]);
      } catch (error) {
        console.error('Error fetching earnings data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEarningsData();
  }, [session]);

  return (
    <ModuleWrapper title="Top Earnings For The Day">
      <ModuleTable
        columns={columns}
        data={data.map((item, index) => ({ ...item, key: `${item.ticker}-${index}` }))}
        loading={loading}
        emptyText="No earnings data available"
      />
    </ModuleWrapper>
  );
};

export default TopEarningsModule;
